import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Utensils, Clock, Users, TrendingUp, CheckCircle, Award } from 'lucide-react';
import LandingNav from '../../components/Landing-Nav';
import FooterLanding from '../../components/FooterLanding';

const HospitalityIndustry = () => {
  const features = [
    { icon: Clock, title: 'Real-Time Attendance', description: 'Track staff clock-ins across all locations instantly' },
    { icon: Users, title: 'Flexible Scheduling', description: 'Adapt to peak hours and special events effortlessly' },
    { icon: TrendingUp, title: 'Employee Cost Analytics', description: 'Optimize staffing levels based on customer traffic' },
    { icon: Award, title: 'Staff Performance', description: 'Monitor and reward top performers automatically' }
  ];

  const benefits = [
    '35% faster shift planning',
    '30% reduction in Employee costs',
    '92% employee retention',
    'Real-time shift swapping',
    'Mobile clock-in/out',
    'Automated break tracking'
  ];

  const stats = [
    { value: '800+', label: 'Restaurants' },
    { value: '35%', label: 'Faster Planning' },
    { value: '92%', label: 'Retention' },
    { value: '$2M+', label: 'Saved Annually' }
  ];

  return (
    <>
      <LandingNav />
      
      <section className="pt-24 pb-16 bg-gradient-to-br from-orange-600 via-red-600 to-pink-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex items-center space-x-2 mb-4">
                <Utensils className="w-8 h-8" />
                <span className="text-orange-200 font-semibold">Hospitality & Food Service</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Elevate Guest Experiences with Efficient Staff Management
              </h1>
              <p className="text-xl text-orange-100 mb-8">
                Streamline your hospitality operations with smart workforce management tools designed for fast-paced, customer-facing environments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup" className="px-8 py-4 bg-white text-orange-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all text-center">
                  Schedule a Demo
                </Link>
                <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all text-center">
                  View Pricing
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <img src="/Timesheet Page.png" alt="Hospitality Dashboard" className="rounded-2xl shadow-2xl border-4 border-white/20" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-orange-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Built for Fast-Paced Environments</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Manage your restaurant, café, or event venue staff with precision</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}>
              <img src="/Analyticspage.png" alt="Analytics" className="rounded-2xl shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Proven Results for Restaurants & Event Venues</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-orange-600 to-pink-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Transform Your Restaurant Operations Today</h2>
          <p className="text-xl text-orange-100 mb-8">Join 800+ successful hospitality businesses</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-4 bg-white text-orange-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
              Schedule Your Free Demo
            </Link>
            <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all">
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default HospitalityIndustry;
